import { defineStore } from 'pinia';
import { Consultation, DTOConsultation, parseConsultation } from '@/store/consultation.interface';
import { DTOSection, parseSection, Section } from '@/store/section.interface';
import { DTOContext, parseContext } from '@/store/context.interface';
import { DTOModule, parseModule } from './module.interface';
import { CustomPage, DTOCustomPage, parseCustomPage } from '@/store/customPage.interface';
import {
  DTOForm,
  parseForm,
  PostFormCommentActionResponse,
  PostFormCommentResponse,
  PostFormVoteResponse,
} from '@/store/form.Interface';
import { diffInDays, todayInTimestamp } from '@/utils/date-util';
import { constant } from '@/constant';
import { DTOLanguageEnabled, LanguageEnabled, LanguageTranslation, Locales } from '@/store/language.interface';
import { User, useUserStore } from '@/store/userStore';
import { DTOProject, parseProject, Project } from './project.interface';
import { StoredFile } from './common.interface';
import { HomepageItem } from './homepageItem.interface';

export interface DTOCommunity extends DTOLanguageEnabled {
  id: number;
  slug: string;
  created_at: string;
  status: number;
  website: string | null;
  title: LanguageTranslation;
  description: LanguageTranslation;
  banner_file: StoredFile;
  logo_file: StoredFile;
  favicon_file: StoredFile;
  contact_info: ContactInfo | null;
  options: CommunityOptions;
  organization_name: string | null;
  projects: Project[];
  featured_project?: DTOFeaturedProject | null;
  default_language: Locales;
  horizontal_logo_file: StoredFile | null;
  footer_description: LanguageTranslation | null;
  default_project_theme_key: string;
  default_page_theme_key: string;
}

export type CommunityBrandingMode = 'default' | 'custom';
export type CommunityBrandingFontSource = 'builtin' | 'remote-css';

export interface CommunityBrandingFontConfig {
  source: CommunityBrandingFontSource;
  family: string;
  builtin_key?: string | null;
  css_url?: string | null;
  fallback?: string | null;
}

export interface CommunityBrandingColorTokens {
  bg?: string | null;
  surface?: string | null;
  surface_raised?: string | null;
  surface_hover?: string | null;
  surface_active?: string | null;
  text?: string | null;
  muted?: string | null;
  text_subtle?: string | null;
  disabled?: string | null;
  brand?: string | null;
  brand_contrast?: string | null;
  link?: string | null;
  link_hover?: string | null;
  border?: string | null;
  border_strong?: string | null;
  focus_ring?: string | null;
  success?: string | null;
  success_bg?: string | null;
  warning?: string | null;
  warning_bg?: string | null;
  danger?: string | null;
  danger_bg?: string | null;
  info?: string | null;
  info_bg?: string | null;
}

export interface CommunityBrandingTypographyTokens {
  font_size_display?: string | null;
  font_size_headline?: string | null;
  font_size_title?: string | null;
  font_size_body?: string | null;
  font_size_small?: string | null;
  leading_display?: string | null;
  leading_headline?: string | null;
  leading_body?: string | null;
  font_size_body_mobile?: string | null;
  leading_body_mobile?: string | null;
  tracking_display?: string | null;
  tracking_headline?: string | null;
}

export interface CommunityBrandingTokens {
  colors?: CommunityBrandingColorTokens | null;
  typography?: CommunityBrandingTypographyTokens | null;
}

export interface CommunityBranding {
  mode?: CommunityBrandingMode;
  preset_key?: string | null;
  tokens?: CommunityBrandingTokens | null;
  fonts?: {
    heading?: CommunityBrandingFontConfig | null;
    body?: CommunityBrandingFontConfig | null;
  } | null;
}

export interface DTOFeaturedProject extends DTOLanguageEnabled {
  banner_file: StoredFile;
  begin_date: Date | null;
  end_date: Date | null;
  id: number;
  slug: string;
  status?: number;
  title: LanguageTranslation;
  visibility: number;
  is_allowed?: boolean;
}

export interface Community extends LanguageEnabled {
  id: number;
  slug: string;
  createdAt: string;
  status: number;
  website: string | null;
  title: LanguageTranslation;
  description: LanguageTranslation;
  bannerFile: StoredFile;
  logoFile: StoredFile;
  faviconFile: StoredFile;
  consultations: Consultation[];
  customPages: CustomPage[];
  projects: Project[];
  contactInfo: ContactInfo | null;
  options: CommunityOptions;
  organizationName: string;
  featuredProject: FeaturedProject | null;
  defaultLanguage: Locales;
  horizontalLogoFile: StoredFile | null;
  footerDescription: LanguageTranslation | null;
  defaultProjectThemeKey: string;
  defaultPageThemeKey: string;
}

export interface CommunityOptions {
  export: {
    pdf: boolean;
  };
  consultation: {
    citizen_proposal: boolean;
    map: boolean;
    moderation_comment: boolean;
  };
  loi96: boolean;
  social_networks: {
    facebook: {
      enabled: boolean;
      url: string;
    };
    instagram: {
      enabled: boolean;
      url: string;
    };
    linkedin: {
      enabled: boolean;
      url: string;
    };
    x: {
      enabled: boolean;
      url: string;
    };
  };
  google_tag_manager: {
    enabled: boolean;
    container_id: string | null;
  };
  featured_project_id: number | null;
  use_demographics: boolean;
  pages: { homepage_id: number | null };
  branding?: CommunityBranding | null;
}

interface ContactInfo {
  phone?: string;
  email?: string;
  address?: LanguageTranslation;
  website?: LanguageTranslation;
}

export interface FeaturedProject extends HomepageItem {}

export const useCommunityStore = defineStore('communityStore', {
  state: () => ({
    community: {} as Community,
    isLoaded: false,
    sectionLoaded: false,
    formsLoaded: false,
    customPageLoaded: false,
  }),
  actions: {
    setCommunity(dtoCommunity: DTOCommunity) {
      this.community = parseCommunity(dtoCommunity);
      this.isLoaded = true;
    },
    setConsultation(consultationDTO: DTOConsultation) {
      if (!this.community.consultations) {
        this.community.consultations = [];
      }
      this.community.consultations.push(parseConsultation(consultationDTO));
    },
    setConsultations(consultationsDTO: DTOConsultation[]) {
      this.community.consultations = consultationsDTO.map(parseConsultation);
    },
    setSections(sectionDTO: DTOSection[], consultationId: number) {
      const consultation = this.community.consultations.find((consultation) => consultation.id === consultationId)!;
      consultation.section = sectionDTO.map(parseSection);
      this.sectionLoaded = true;
    },
    setConsultationContext(contextDTO: DTOContext[], consultationId: number) {
      const consultation = this.community.consultations.find((consultation) => consultation.id === consultationId)!;
      consultation.context = contextDTO.map(parseContext);
    },
    setCustomPage(customPageDTO: DTOCustomPage[]) {
      this.community.customPages = customPageDTO.map(parseCustomPage);
      // this.community.customPages = parseCustomPage(customPageDTO)
      this.customPageLoaded = true;
    },
    setProject(projectDTO: DTOProject) {
      if (!this.community.projects) {
        this.community.projects = [];
      }
      this.community.projects.push(parseProject(projectDTO));
    },
    setProjects(projectsDTO: DTOProject[]) {
      this.community.projects = projectsDTO.map(parseProject);
    },
    setProjectModules(modulesDTO: DTOModule[], projectId: number) {
      const project = this.community.projects.find((project) => project.id === projectId);
      project.modules = modulesDTO.map(parseModule);
    },
    setSectionContext(contextDTO: DTOContext[], consultationId: number, sectionId: number) {
      const consultation = this.community.consultations.find((consultation) => consultation.id === consultationId)!;
      const section = consultation.section.find((s) => s.id === sectionId)!;
      section.context = contextDTO.map(parseContext);
    },
    setSectionForms(formDTO: DTOForm[], section: Section) {
      section.form = formDTO.map(parseForm);
    },
    getConsultationTimeLeft(consultation: Consultation): number {
      const startDate = consultation.beginDate ? consultation.beginDate : todayInTimestamp();
      const endDate = consultation.endDate;
      const diffDays = diffInDays(startDate, endDate);
      if (diffDays <= 0) {
        return 0;
      }
      return diffDays;
    },
    addVoteToFormById(response: PostFormVoteResponse) {
      const consultation = this.community.consultations.find((c) => c.id === response.consultationId);
      const section = consultation.section.find((s) => s.id === response.sectionId);
      const form = section.form.find((f) => f.id === response.formId);
      form.response[0] = response.vote;
      form.statistiques = response.statistiques;
    },
    addCommentToFormById(response: PostFormCommentResponse) {
      const consultation = this.community.consultations.find((c) => c.id === response.consultationId);
      const section = consultation.section.find((s) => s.id === response.sectionId);
      const form = section.form.find((f) => f.id === response.formId);
      form.comments.unshift(response.comment);
    },
    updateCommentAction(response: PostFormCommentActionResponse) {
      const consultation = this.community.consultations.find((c) => c.id === response.consultationId);
      const section = consultation.section.find((s) => s.id === response.sectionId);
      const form = section.form.find((f) => f.id === response.formId);
      const comment = form.comments.find((c) => c.id === response.commentId);
      // A map form keeps its pins in `mapMarkers` and its `comments` empty, so a pin's like finds nothing here. Throwing
      // would turn that saved like into a failed one, and useFormMapMarkers already shows it on the pin.
      if (!comment) return;
      switch (response.action) {
        case 'like':
          comment.likes.push({ action: 1, userId: response.userId });
          if (form.type === 5) {
            const this_marker = form.mapMarkers.find((marker) => marker.comment_id === response.commentId);
            this_marker.likes.push({ action: 1, userId: response.userId });
          }
          break;
        case 'flag':
          comment.flags.push({ action: 2, userId: response.userId });
          break;
        case 'unlike':
          const likeActionIndex = comment.likes.findIndex((a) => a.userId === response.userId);
          comment.likes.splice(likeActionIndex, 1);
          break;

        case 'unflag':
          const flagActionIndex = comment.flags.findIndex((a) => a.userId === response.userId);
          comment.flags.splice(flagActionIndex, 1);
          break;
      }
    },
    async getCustomPageBySlug(slug: string): Promise<CustomPage> {
      return this.community.customPages.find((customPage: any) => customPage.slug === slug);
    },
    isUserAllowedAccessConsultation(consultation: Consultation, user: User) {
      const userStore = useUserStore();
      const isOngoingOrCompleted = [
        constant.Consultation.Status.Ongoing,
        constant.Consultation.Status.Completed,
      ].includes(consultation.status);
      if (consultation.type === constant.Consultation.Type.Public && isOngoingOrCompleted) {
        return { allowed: true };
      }
      if (!user.email) {
        return { allowed: false, reason: 'login' };
      }
      if (userStore.isAdmin() || userStore.isEditor(constant.HomePageItem.Type.Consultation, consultation.id)) {
        return { allowed: true };
      }
      if (consultation.isAllowed && isOngoingOrCompleted) {
        return { allowed: true };
      } else {
        return { allowed: false, reason: 'NotAllowedByAdmin' };
      }
    },
    isUserAllowedAccessProject(project: Project, user: User) {
      const userStore = useUserStore();
      const isOngoingOrCompleted = [constant.Project.Status.Ongoing, constant.Project.Status.Completed].includes(
        project.status,
      );
      if (project.visibility === constant.Project.Type.Public && isOngoingOrCompleted) {
        return { allowed: true };
      } else if (!user.email) {
        return { allowed: false, reason: 'login' };
      } else if (userStore.isAdmin() || userStore.isEditor(constant.HomePageItem.Type.Project, project.id)) {
        return { allowed: true };
      }
      if (project.isAllowed && isOngoingOrCompleted) {
        return { allowed: true };
      }
      return { allowed: false, reason: 'NotAllowedByAdmin' };
    },
    isConsultationCompleted(consultation: Consultation) {
      return consultation.status === constant.Consultation.Status.Completed;
    },
    isUserAllowedIfPrelaunchConsultation(consultation: Consultation) {
      const userStore = useUserStore();
      const isPrelaunch = consultation.status === constant.Consultation.Status.PreLaunch;
      return (
        !isPrelaunch ||
        userStore.isAdmin() ||
        userStore.isEditor(constant.HomePageItem.Type.Consultation, consultation.id)
      );
    },
    addProjectTemplate(projectTemplate: DTOProject) {
      if (!this.community.projects) {
        this.community.projects = [];
      }
      this.community.projects.push(parseProject(projectTemplate));
    },
  },
  getters: {
    getSections(state) {
      return (consultationId: number) =>
        state.community?.consultations?.find((consultation) => consultation.id === consultationId)?.section;
    },
    getProject(state) {
      return (projectId: number) => state.community?.projects?.find((project) => project.id === projectId);
    },
    isCommunityGtmEnabled: (state) => {
      return state.community?.options?.google_tag_manager?.enabled ?? false;
    },
    getCommunityGtmId: (state) => {
      return state.community?.options?.google_tag_manager?.container_id ?? null;
    },
  },
});

function parseCommunity(dtoCommunity: DTOCommunity): Community {
  return {
    id: dtoCommunity.id,
    slug: dtoCommunity.slug,
    createdAt: dtoCommunity.created_at,
    status: dtoCommunity.status,
    website: dtoCommunity.website,
    title: dtoCommunity.title,
    description: dtoCommunity.description,
    bannerFile: dtoCommunity.banner_file,
    logoFile: dtoCommunity.logo_file,
    faviconFile: dtoCommunity.favicon_file,
    consultations: [],
    customPages: [],
    languageEnabled: dtoCommunity.language_enabled,
    contactInfo: dtoCommunity.contact_info,
    options: dtoCommunity.options,
    organizationName: dtoCommunity.organization_name ?? '',
    projects: dtoCommunity.projects,
    featuredProject: dtoCommunity.featured_project
      ? {
          itemType: constant.HomePageItem.Type.Project,
          id: dtoCommunity.featured_project.id,
          slug: dtoCommunity.featured_project.slug,
          status: dtoCommunity.featured_project.status,
          visibility: dtoCommunity.featured_project.visibility,
          bannerFile: dtoCommunity.featured_project.banner_file,
          title: dtoCommunity.featured_project.title,
          beginDate: dtoCommunity.featured_project?.begin_date
            ? new Date(dtoCommunity.featured_project.begin_date)
            : null,
          endDate: dtoCommunity.featured_project?.end_date ? new Date(dtoCommunity.featured_project.end_date) : null,
          isAllowed: dtoCommunity.featured_project.is_allowed,
          languageEnabled: dtoCommunity.featured_project.language_enabled,
        }
      : null,
    defaultLanguage: dtoCommunity.default_language,
    horizontalLogoFile: dtoCommunity.horizontal_logo_file,
    footerDescription: dtoCommunity.footer_description,
    defaultProjectThemeKey: dtoCommunity.default_project_theme_key ?? 'default',
    defaultPageThemeKey: dtoCommunity.default_page_theme_key ?? 'default',
  };
}
